// File: src/components/Upload/UploadZone.js
import React, { useState, useRef, useCallback } from 'react';
import { Upload, FileText, AlertCircle, Zap, Shield, Clock } from 'lucide-react';
import styles from './UploadZone.module.css';

const UploadZone = ({ onFileUpload, isProcessing = false, className = '' }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // File validation
  const validateFile = useCallback((file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'application/pdf',
      'image/jpeg', 
      'image/jpg', 
      'image/png',
      'image/heic',
      'image/heif'
    ];
    
    if (file.size > maxSize) {
      return 'File size must be less than 10MB';
    }
    
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      return 'Please upload a PDF, JPG, PNG, or HEIC file';
    }
    
    return null;
  }, []);

  // Drag handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelection(files[0]);
    }
  }, []);

  // File selection handler
  const handleFileSelection = useCallback((file) => {
    const validationError = validateFile(file);
    
    if (validationError) {
      setError(validationError);
      setSelectedFile(null);
      return;
    }
    
    setError(null);
    setSelectedFile(file);
  }, [validateFile]);

  const handleFileInputChange = useCallback((e) => {
    const files = e.target.files;
    if (files && files[0]) {
      handleFileSelection(files[0]);
    }
  }, [handleFileSelection]);

  const handleUpload = useCallback(() => {
    if (selectedFile && onFileUpload && !isProcessing) {
      onFileUpload(selectedFile);
    }
  }, [selectedFile, onFileUpload, isProcessing]);

  const resetSelection = useCallback(() => {
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getFileTypeIcon = (file) => {
    if (file.type === 'application/pdf') return '📄';
    if (file.type.startsWith('image/')) return '🖼️';
    return '📁';
  };

  return (
    <div className={`${styles.uploadContainer} ${className}`}>
      <div 
        className={`
          ${styles.uploadZone} 
          ${dragActive ? styles.dragActive : ''} 
          ${selectedFile ? styles.hasFile : ''} 
          ${error ? styles.hasError : ''}
          ${isProcessing ? styles.isProcessing : ''}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !selectedFile && !isProcessing && fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-label="Upload insurance policy file"
      >
        <input
          ref={fileInputRef}
          type="file"
          className={styles.fileInput}
          accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
          onChange={handleFileInputChange}
          disabled={isProcessing}
          aria-label="Choose file to upload"
        />

        {!selectedFile ? (
          <div className={styles.uploadPrompt}>
            <div className={styles.uploadIcon}>
              <Upload size={48} />
            </div>
            <h3 className={styles.uploadTitle}>
              Drop Your Insurance Policy Here
            </h3>
            <p className={styles.uploadDescription}>
              PDF, JPG, PNG, HEIC - We'll scan it in seconds and give you the brutal truth
            </p>
            <button 
              className={styles.browseButton} 
              type="button"
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Choose File to Crush'}
            </button>
            <div className={styles.uploadHint}>
              Maximum file size: 10MB
            </div>
          </div>
        ) : (
          <div className={styles.filePreview}>
            <div className={styles.fileHeader}>
              <div className={styles.fileIcon}>
                <span className={styles.fileEmoji}>{getFileTypeIcon(selectedFile)}</span>
                <FileText size={20} />
              </div>
              <div className={styles.fileInfo}>
                <h4 className={styles.fileName}>{selectedFile.name}</h4>
                <p className={styles.fileSize}>{formatFileSize(selectedFile.size)}</p>
              </div>
            </div>
            
            <div className={styles.fileActions}>
              <button 
                className={styles.uploadButton}
                onClick={handleUpload}
                disabled={isProcessing}
              >
                <Zap size={20} />
                {isProcessing ? 'Crushing...' : 'Crush My Rates Now!'}
              </button>
              <button 
                className={styles.resetButton}
                onClick={resetSelection}
                type="button"
                disabled={isProcessing}
              >
                Choose Different File
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className={styles.errorMessage}>
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* Security & Speed Indicators */}
      <div className={styles.trustIndicators}>
        <div className={styles.trustItem}>
          <Shield size={16} />
          <span>Your policy is encrypted and automatically deleted after analysis</span>
        </div>
        <div className={styles.trustItem}>
          <Clock size={16} />
          <span>Analysis typically takes 15-30 seconds</span>
        </div>
        <div className={styles.trustItem}>
          <Zap size={16} />
          <span>Powered by advanced AI document analysis</span>
        </div>
      </div>
    </div>
  );
};

export default UploadZone;